import type { ReactNode } from 'react';
import { Card, Stack, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { TenantName } from '../../../../components/TenantName';
import { CopyableId } from '../../../../components/CopyableId';
import { RunStatusBadge } from '../../components/RunStatusBadge';
import { formatDuration } from '../../lib/duration';
import { useT } from '../../../../i18n/I18nProvider';
import '../../flows.css';

type FlowRun = NonNullable<Awaited<ReturnType<typeof admin.listFlowRuns>>['data']>[number];

/**
 * The strip of facts under the page title: state, timing, references and
 * routing, in four groups.
 *
 * Everything here is what a person needs before choosing a tab, which is why
 * it is a strip and not the first card of the Overview. The ids sit behind
 * copy buttons rather than being read out, because they are quoted into
 * tickets and never scanned.
 */
export function RunHeader({ run }: { run: FlowRun }) {
  const t = useT();
  const running = !run.finished_at;

  return (
    <Card withBorder>
      <Stack gap="md">
        <div className="rr-run-summary">
          <div className="rr-run-summary__col">
            <Field label={t('flows.view.summary.state')}>
              <Group gap="xs" align="center" wrap="wrap">
                <RunStatusBadge status={run.status} size="sm" />
                {running && <Badge size="xs" variant="dot" color="blue">{t('flows.view.run.live')}</Badge>}
              </Group>
            </Field>
            <Field label={t('flows.view.summary.duration')}>
              <Text role="bodySmall" fw={600}>{formatDuration(run.started_at, run.finished_at)}</Text>
              {running && (
                <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.view.summary.updating')}</Text>
              )}
            </Field>
          </div>

          <div className="rr-run-summary__col">
            <Field label={t('flows.columns.startedAt')}>
              <Text role="bodySmall" fw={600}>{formatDateTime(run.started_at)}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{formatRelative(run.started_at)}</Text>
            </Field>
            <Field label={t('flows.view.summary.finished')}>
              {run.finished_at
                ? (
                  <>
                    <Text role="bodySmall" fw={600}>{formatDateTime(run.finished_at)}</Text>
                    <Text role="caption" c="var(--rr-color-text-muted)">{formatRelative(run.finished_at)}</Text>
                  </>
                )
                : <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('flows.view.summary.inProgress')}</Text>}
            </Field>
          </div>

          <div className="rr-run-summary__col">
            <Field label={t('flows.view.overview.runId')}>
              <CopyableId value={run.id} label={t('flows.copyRunId')} chars={14} />
            </Field>
            <Field label={t('flows.view.overview.flowId')}>
              <CopyableId value={run.flow_id} label={t('flows.copyFlowId')} chars={14} />
            </Field>
            <Field label={t('flows.view.summary.conversation')}>
              {run.conversation_id
                ? <CopyableId value={run.conversation_id} label={t('flows.copyConversationId')} chars={14} />
                : <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.view.overview.noConversation')}</Text>}
            </Field>
          </div>

          <div className="rr-run-summary__col">
            <Field label={t('common.tenant')}>
              <TenantName id={run.organisation_id} />
            </Field>
            <Field label={t('flows.columns.channel')}>
              <Text role="bodySmall" fw={600}>{run.channel}</Text>
            </Field>
            <Field label={t('flows.view.summary.contact')}>
              {run.contact_msisdn
                ? <Text role="bodySmall" fw={600}>{run.contact_msisdn}</Text>
                : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.notAvailable')}</Text>}
            </Field>
          </div>
        </div>

        {run.error && (
          <Alert color="error" variant="light" title={t('flows.view.run.errorTitle')}>
            {run.error}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      {children}
    </div>
  );
}
